import { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * VersionCheck component that detects when a new version of the app is deployed
 * and prompts the user to refresh the page.
 *
 * This works by fetching the version.json file from the server and comparing it
 * with the version stored in localStorage.
 */
function VersionCheck() {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Function to fetch and check for updates
    const checkForUpdates = async () => {
      try {
        // Fetch the version.json file with cache-busting timestamp
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
        });

        if (!response.ok) {
          console.warn('Failed to fetch version.json:', response.status);
          return;
        }

        const data = await response.json();
        const currentBuildNumber = data.buildNumber || '0';
        const storedVersion = localStorage.getItem('app_version');

        // console.log('Stored version:', storedVersion, 'Current version:', currentBuildNumber);

        if (!storedVersion) {
          // First time loading, store the current version number
          localStorage.setItem('app_version', currentBuildNumber);
        } else if (storedVersion !== currentBuildNumber) {
          // Version number has changed, indicating a new deployment
          console.log('Version mismatch detected! Stored:', storedVersion, 'Current:', currentBuildNumber);
          setShowUpdatePrompt(true);
        }
      } catch (error) {
        console.error('Error checking for updates:', error);
      }
    };

    // Initialize: check and store current version on mount
    checkForUpdates();

    // Check for updates every 10 seconds
    const interval = setInterval(checkForUpdates, 10 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const handleRefresh = async () => {
    try {
      // Fetch the current version before updating localStorage
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      });
      if (response.ok) {
        const data = await response.json();
        const currentBuildNumber = data.buildNumber || '0';
        localStorage.setItem('app_version', currentBuildNumber);
      }
    } catch (error) {
      console.error('Error fetching version:', error);
    }
    // Clear cache and reload
    window.location.reload(true);
  };

  const handleDismiss = () => {
    setShowUpdatePrompt(false);
    // Update the stored version number to the current version
    // This prevents showing the prompt again until the next actual update
    // localStorage.setItem('app_version', currentBuildNumber);
  };

  if (!showUpdatePrompt) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: '#4CAF50',
        color: 'white',
        padding: '12px 20px',
        textAlign: 'center',
        zIndex: 10000,
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <div style={{ flex: 1 }}>
        <strong>{t('versionCheck.newVersionAvailable')}</strong> {t('versionCheck.refreshMessage')}
      </div>
      <div style={{ display: 'flex', gap: '10px', marginLeft: '20px' }}>
        <button
          onClick={handleRefresh}
          style={{
            backgroundColor: 'white',
            color: '#4CAF50',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
        >
          {t('versionCheck.refreshNow')}
        </button>
        <button
          onClick={handleDismiss}
          style={{
            backgroundColor: 'transparent',
            color: 'white',
            border: '1px solid white',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {t('versionCheck.later')}
        </button>
      </div>
    </div>
  );
}

export default VersionCheck;

